import {
  AddFieldParam,
  WidgetContainer,
} from '@/components/business/crud/widgets/WidgetContainer';
import { computed, h, reactive, VNode } from 'vue';
import { ColumnType } from 'ant-design-vue/lib/table/interface';
import { FieldType } from '@/components/business/crud/interface/FieldType';
import { Dropdown, Menu, MenuItem } from 'ant-design-vue';
import ScBtn from '@/components/common/widget/ScBtn.vue';
import { WidgetType } from '@/components/business/crud/interface/WidgetType';

type FieldSortSetting = 'asc' | 'desc' | 'none' | 'disabled';

interface TableFieldExtraSetting {
  // 字段排序：asc正序，desc逆序，none未排序，disabled不能排序
  sort: FieldSortSetting;
}

interface TableWidgetContainerSetting {
  selectedRows: any[];
  buttons: {
    [id: string]: ButtonSetting;
  };
  columnEdit?: ColumnEdit
}

type ButtonType = 'row' | 'table' | 'select';

export interface ButtonSetting {
  type: ButtonType;
  icon?: string;
  text?: string;
  tip?: string;
  enable: boolean;
  // 是否隐藏
  hidden?: ((data: any) => boolean) | boolean;
  // 触发事件
  handler: (data: any) => Promise<void> | void;
  // 确认文字
  confirm?: ((data: any) => string | undefined) | string;
}

export type ColumnEdit = (
  row: any,
  propName: string,
  value: any
) => Promise<boolean>;

export class TableWidgetContainer extends WidgetContainer<
  TableFieldExtraSetting,
  WidgetType
> {
  public readonly tableSetting: TableWidgetContainerSetting = reactive({
    selectedRows: [],
    buttons: {},
  });

  public readonly buttons = computed(() => {
    const tableButtons = Object.values(this.tableSetting.buttons).filter(
      (button) => button.enable && button.type === 'table'
    );

    const selectedButtons: ButtonSetting[] = Object.values(
      this.tableSetting.buttons
    )
      .filter((button) => button.enable && button.type === 'select')
      .map((button) => ({
        ...button,
        handler: async () => {
          try {
            await button.handler(this.tableSetting.selectedRows)
          } finally {
            this.tableSetting.selectedRows = []
          }
        },
        confirm:
          typeof button.confirm === 'function'
            ? button.confirm(this.tableSetting.selectedRows)
            : button.confirm,
      }));
    return [...tableButtons, ...selectedButtons];
  });

  public readonly columns = computed(() => {
    const columns: ColumnType[] = [];
    Object.entries(this.widgetItemMap.value)
      .sort((a, b) => a[1].order - b[1].order)
      .map(([id, item]) => {
        columns.push({
          title: item.title,
          dataIndex: item.id,
          ...this.getFieldSortSetting(id),
          customRender: (data: any) => {
            return h(item.component.value, {
              ...item.props,
              data: data.record,
              columnEdit: this.tableSetting.columnEdit,
            });
          },
        });
      });
    if (
      Object.values(this.tableSetting.buttons).find(
        (button) => button?.type === 'row'
      )
    ) {
      columns.push({
        title: '操作',
        fixed: 'right',
        customRender: (data: any) => this.renderRowButtons(data.record),
      });
    }
    return columns;
  });

  addField(
    propName: string,
    title: string,
    widget: FieldType,
    param?: AddFieldParam & { sort?: FieldSortSetting }
  ) {
    const extra = { sort: param?.sort || 'disabled' };
    super.doAddField(propName, title, widget, extra, param);
  }

  protected renderRowButtons(data: any) {
    const visibleRowButtons = this.getVisibleRowButtons(data);
    const vNodes: VNode[] = [];
    visibleRowButtons.rowButtons.map((b, key) => {
      vNodes.push(this.renderRowButton(b, data, key));
    });
    const moreMenuItems: VNode[] = [];
    visibleRowButtons.rowMoreMenu.map((b, key) => {
      moreMenuItems.push(
        h(MenuItem, { key }, this.renderRowButton(b, data, key))
      );
    });
    if (moreMenuItems.length > 0) {
      vNodes.push(
        h(
          Dropdown,
          { trigger: ['click'] },
          {
            default: h(
              'a',
              {
                class: 'ant-dropdown-link',
                onClick: (e: MouseEvent) => e.preventDefault(),
              },
              '更多'
            ),
            overlay: h(Menu),
          }
        )
      );
    }
    return h('div', vNodes);
  }

  protected renderRowButton(
    b: ButtonSetting,
    data: any,
    key?: string | number
  ) {
    return h(ScBtn, {
      type: 'link',
      key,
      size: 'small',
      text: b.text,
      icon: b.icon,
      handler: () => b.handler(data),
      confirm: typeof b.confirm === 'function' ? b.confirm(data) : b.confirm,
    });
  }

  protected getVisibleRowButtons(data: any) {
    const buttons = Object.values(this.tableSetting.buttons)
      .filter((button) => button.type === 'row')
      .filter((button) => button.enable)
      .map((button) => ({
        ...button,
        hidden:
          typeof button.hidden === 'function'
            ? button.hidden(data)
            : button.hidden,
      }))
      .filter((button) => !button.hidden);
    const rowButtons: ButtonSetting[] = [];
    const rowMoreMenu: ButtonSetting[] = [];
    const buttonNum = 3;
    if (buttons.length <= buttonNum) {
      rowButtons.push(...buttons);
    } else {
      rowButtons.push(...buttons.slice(0, buttonNum - 1));
      rowMoreMenu.push(...buttons.slice(buttonNum - 1));
    }
    return { rowButtons, rowMoreMenu };
  }

  private getFieldSortSetting(id: string) {
    const extra = this.getExtra(id);
    if (extra && extra.sort !== 'disabled') {
      const sortOrder: 'ascend' | 'descend' | undefined =
        extra.sort === 'asc'
          ? 'ascend'
          : extra.sort === 'desc'
          ? 'descend'
          : undefined;
      return { sorter: true, sortOrder };
    }
    return {};
  }

  addButton(
    id: string,
    param: Omit<ButtonSetting, 'enable'> &
      Partial<Pick<ButtonSetting, 'enable'>>
  ) {
    this.tableSetting.buttons[id] = Object.assign(
      {
        enable: true,
      },
      param
    );
  }

  hideButtons(...ids: string[]) {
    ids.map((id) => {
      this.tableSetting.buttons[id].enable = false;
    });
  }

  showButtons(...ids: string[]) {
    ids.map((id) => {
      this.tableSetting.buttons[id].enable = true;
    });
  }

  hideButtonsExclude(...ids: string[]) {
    Object.keys(this.tableSetting.buttons).map((id) => {
      if (!ids.includes(id)) {
        this.tableSetting.buttons[id].enable = false;
      } else {
        this.tableSetting.buttons[id].enable = true;
      }
    });
  }
}
